/**
 * Web Vitals de campo → dataLayer (evento `web_vitals`), sem dependência.
 *
 * Por quê: o CrUX zerou para a origem em ago/2026 (PageSpeed devolve
 * originLoadingExperience vazio) — sem isto, nenhuma melhoria de LCP/CLS/INP é
 * comprovável em campo. O GTM consome `web_vitals` e manda para o GA4
 * (docs/rotinas/medicao-gtm-checklist.md). Respeita o Consent Mode: é o GTM que
 * decide se o ping sai com cookie ou não; aqui só medimos.
 *
 * O que mede (aproximação honesta, ~1,5 KB em vez dos ~6 KB da lib oficial):
 *  - LCP: último `largest-contentful-paint` antes da 1ª interação/ocultação (spec).
 *  - CLS: janelas de sessão (gap 1 s, máx. 5 s), maior janela (spec), só shifts
 *         sem input recente.
 *  - INP: maior `event` (duração ≥ 40 ms) — igual ao p98 quando a página tem
 *         menos de ~50 interações, que é o caso deste site.
 *  - TTFB: `responseStart` da navegação.
 * Cada métrica sai UMA vez, em `visibilitychange`→hidden ou `pagehide` (é quando
 * o valor é final). Não roda de novo na navegação AJAX (LCP não existe ali);
 * `page_path` é o da carga inicial. Não mede em bfcache restore.
 *
 * ⚠️ Os cortes por ocultação NÃO são detalhe, e são DOIS, diferentes de propósito:
 * `primeiraOcultacao` para o LCP (métrica de carga) e `inpValido` por sobreposição
 * para o INP (métrica de interação). Sem eles, aba em segundo plano injetava LCP e
 * INP de dezenas de segundos e destruía qualquer média — medido em 2026-08-24
 * (LCP `poor` méd. 54,13 s; INP `poor` méd. 12,22 s). Ver os comentários de cada
 * guarda antes de "simplificar" as duas numa só: já foi considerado e não dá.
 *
 * Debug: `window.__odontoWebVitals` expõe os valores correntes.
 * Adicionado 2026-08-17 (revisão com skills, §5-10).
 */
(function () {
    'use strict';
    if (!('PerformanceObserver' in window) || typeof PerformanceObserver.supportedEntryTypes === 'undefined') {
        return;
    }

    const supported = PerformanceObserver.supportedEntryTypes;
    const pagePath = location.pathname || '/';
    const state = { LCP: null, CLS: 0, INP: null, TTFB: null };
    const reported = {};
    window.__odontoWebVitals = state;

    // Limiares oficiais (bom / precisa melhorar / ruim) para o rating no relatório.
    const limites = { LCP: [2500, 4000], CLS: [0.1, 0.25], INP: [200, 500], TTFB: [800, 1800] };
    const rating = (name, v) => v <= limites[name][0] ? 'good' : (v <= limites[name][1] ? 'needs-improvement' : 'poor');
    const idPagina = 'v1-' + Date.now() + '-' + Math.floor(Math.random() * 1e6);

    /**
     * Momento em que a página ficou oculta pela PRIMEIRA vez — e LCP registrado
     * depois disso é descartado. É a guarda `firstHiddenTime` da lib oficial, e
     * a ausência dela foi medida em 2026-08-24: aba que nasce em segundo plano
     * (Ctrl+clique, "abrir em nova aba", sessão restaurada) só pinta quando o
     * usuário a foca, minutos depois, e gravava um LCP de dezenas de segundos.
     *
     * O estrago nos 8 dias de coleta: 51 dos 609 LCP (8,4%) caíram em `poor` com
     * média de **54,13 s**, contra 0,64 s da faixa `good` — e esses 8,4%
     * respondiam por 88% da soma, inutilizando qualquer média. O que provou não
     * ser rede foi o TTFB: 3 eventos `poor` contra 51 do LCP. Conexão ruim
     * apareceria nos dois.
     *
     * Página que JÁ nasce oculta tem `0` aqui, então nenhum LCP é aceito e a
     * métrica simplesmente não é enviada — que é o comportamento correto, o
     * mesmo do CrUX. Métrica não medida não vira "LCP alto".
     */
    let primeiraOcultacao = document.visibilityState === 'hidden' ? 0 : Infinity;

    /**
     * Instantes em que a aba foi ocultada. Usado SÓ pelo INP — ver `inpValido`.
     * Teto de 20 para não crescer sem limite numa aba que fica horas aberta;
     * quem passa disso são ciclos antigos, irrelevantes para a interação atual.
     */
    const ocultacoes = [];

    // Registrado ANTES do listener de flush, de propósito: listeners no mesmo
    // alvo disparam na ordem de registro, então este atualiza os cortes antes de
    // o flush ler o state.
    addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            const agora = performance.now();
            primeiraOcultacao = Math.min(primeiraOcultacao, agora);
            if (ocultacoes.length < 20) { ocultacoes.push(agora); }
        }
    }, { capture: true });

    // LCP é métrica de CARGA: se a página ficou oculta antes da pintura, o valor
    // não vale, ponto. Corte simples pelo primeiro instante de ocultação.
    const lcpValido = (entry) => entry.startTime < primeiraOcultacao;

    /**
     * INP precisa de um corte DIFERENTE do LCP — e reusar o do LCP seria um bug.
     *
     * `primeiraOcultacao` vale 0 numa aba que nasce em segundo plano. Para o LCP
     * isso é o comportamento certo (não houve carga visível para medir), mas para
     * o INP descartaria TODA interação feita depois de o usuário focar a aba —
     * que é exatamente o dado que queremos. Seria trocar um viés por uma cegueira.
     *
     * O defeito real do INP é outro: `duration` de um `event` vai do gesto até a
     * PRÓXIMA PINTURA, e aba oculta não pinta. Interação cuja pintura estava em
     * curso quando a aba foi ocultada fica com a duração inflada pelo tempo em
     * que a aba passou em segundo plano. Medido em 2026-08-24, antes desta
     * guarda: 4 eventos `poor` com média de **12,22 s** — 12 segundos não é uma
     * interação, é uma aba que voltou.
     *
     * Então o corte é por SOBREPOSIÇÃO: descarta a interação se algum instante de
     * ocultação cai dentro de [início, início+duração]. Interação que começou e
     * pintou inteira com a aba visível passa, mesmo que a aba tenha sido ocultada
     * antes ou depois.
     *
     * Limitação aceita: guardamos no máximo 20 ocultações. Aba com mais ciclos que
     * isso pode deixar passar uma interação inflada por um ciclo antigo — caso
     * raro e de custo baixo perto de crescer memória sem teto.
     */
    const inpValido = (entry) => {
        const fim = entry.startTime + entry.duration;
        for (let i = 0; i < ocultacoes.length; i++) {
            if (entry.startTime <= ocultacoes[i] && ocultacoes[i] <= fim) { return false; }
        }
        return true;
    };

    const push = (name, value) => {
        if (value === null || value === undefined || reported[name]) { return; }
        reported[name] = true;
        const v = name === 'CLS' ? Math.round(value * 1000) / 1000 : Math.round(value);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'web_vitals',
            metric_name: name,
            metric_value: v,
            metric_rating: rating(name, value),
            metric_id: idPagina + '-' + name,
            page_path: pagePath
        });
    };

    // --- TTFB -------------------------------------------------------------
    try {
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav && nav.responseStart > 0) { state.TTFB = nav.responseStart; }
    } catch (e) { /* sem navigation timing: segue sem TTFB */ }

    // --- LCP --------------------------------------------------------------
    let lcpObs = null;
    if (supported.indexOf('largest-contentful-paint') !== -1) {
        lcpObs = new PerformanceObserver((list) => {
            // Filtra ANTES de pegar o último: a última entrada pode ser posterior
            // à ocultação (aba de fundo que pintou tarde) e não vale.
            const validas = list.getEntries().filter(lcpValido);
            const last = validas[validas.length - 1];
            if (last) { state.LCP = last.startTime; }
        });
        lcpObs.observe({ type: 'largest-contentful-paint', buffered: true });
        // A 1ª interação congela o LCP (spec): para de observar e reporta.
        const congelaLcp = () => {
            if (lcpObs) { lcpObs.takeRecords().filter(lcpValido).forEach(e => { state.LCP = e.startTime; }); lcpObs.disconnect(); lcpObs = null; }
            push('LCP', state.LCP);
        };
        ['keydown', 'click', 'pointerdown'].forEach(t => addEventListener(t, congelaLcp, { once: true, capture: true, passive: true }));
    }

    // --- CLS (janelas de sessão) ------------------------------------------
    if (supported.indexOf('layout-shift') !== -1) {
        let sessao = 0, inicio = 0, ultimo = 0;
        const clsObs = new PerformanceObserver((list) => {
            list.getEntries().forEach((e) => {
                if (e.hadRecentInput) { return; }
                if (sessao && e.startTime - ultimo < 1000 && e.startTime - inicio < 5000) {
                    sessao += e.value;
                } else {
                    sessao = e.value;
                    inicio = e.startTime;
                }
                ultimo = e.startTime;
                if (sessao > state.CLS) { state.CLS = sessao; }
            });
        });
        clsObs.observe({ type: 'layout-shift', buffered: true });
    }

    // --- INP (aprox.: maior interação) ------------------------------------
    if (supported.indexOf('event') !== -1) {
        try {
            const inpObs = new PerformanceObserver((list) => {
                list.getEntries().forEach((e) => {
                    if (!e.interactionId) { return; }
                    // Descarta interação cuja pintura atravessou uma ocultação da aba
                    // (senão o INP vira "tempo que a aba passou em segundo plano").
                    if (!inpValido(e)) { return; }
                    if (state.INP === null || e.duration > state.INP) { state.INP = e.duration; }
                });
            });
            inpObs.observe({ type: 'event', buffered: true, durationThreshold: 40 });
        } catch (e) { /* durationThreshold não suportado: sem INP */ }
    }

    // --- Envio: quando a página some (valor final) --------------------------
    const flush = () => {
        if (lcpObs) { lcpObs.takeRecords().filter(lcpValido).forEach(e => { state.LCP = e.startTime; }); }
        push('TTFB', state.TTFB);
        push('LCP', state.LCP);
        push('CLS', state.CLS);
        push('INP', state.INP);
    };
    addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') { flush(); } }, { capture: true });
    addEventListener('pagehide', flush, { capture: true });
})();
